import type {MouseEvent} from 'react';
import {useRouter} from 'next/router';
import {Button, type ButtonProps} from '@chakra-ui/button';
import {useToast} from '@chakra-ui/toast';

import {useTranslate} from '@eksab/i18n';
import {useAccessToken} from '@eksab/hooks/useAccessToken';
import {BackIcon} from '@eksab/assets/icons/BackIcon';
import {getCountry, getLanguage} from '@eksab/util';

import {useGenerateGuestTokenMutation, useGuestStore, useJoinChallengeMutation} from '@eksab/features/trivia/hooks';
import {useTriviaStore} from '@eksab/features/trivia/state';
import type {Challenge} from '@eksab/features/trivia/types';
import {games} from '@eksab/features/play/const';
import {useEmbedded} from '@eksab/hooks/useEmbedded';

interface JoinCTAProps extends ButtonProps {
  challenge: Challenge;
}

export function JoinCTA({challenge, ...props}: JoinCTAProps) {
  const toast = useToast();
  const {locale, replace} = useRouter();

  const t = useTranslate();
  const isLoggedIn = useAccessToken().isSuccess;
  const {isEmbedded} = useEmbedded();

  const onChallengeJoined = useTriviaStore((state) => state.onChallengeJoined);
  const generateGuestTokenMutation = useGenerateGuestTokenMutation({
    onSuccess: (res) => useGuestStore.setState({id: res.extra.guest_id, token: res.access_token}),
    onError: (e) => toast({status: 'error', title: e.response?.data.message ?? t('app.errors.try-again')}),
  });
  const joinChallengeMutation = useJoinChallengeMutation(challenge.id, {
    onSuccess: ({attempt_id: attemptId, first_question: firstQuestion}) => {
      mixpanel?.track('Join Challenge', {
        id: challenge.id,
        challenge: challenge.title,
        challenge_topic: challenge.topic,
        entry_fee: challenge.entry_fee,
        prize_type: challenge.prize?.type,
        prize_amount: challenge.prize?.amount,
      });

      onChallengeJoined({game: games[challenge.game], challenge, attemptId, firstQuestion});
      toast({status: 'success', title: t('trivia.join.success'), duration: 2500});

      const selectedLanguage = useTriviaStore.getState().language!;
      replace('/trivia/play', undefined, {locale: `${selectedLanguage}-${getCountry(locale)}`});
    },
    onError: (e) => {
      let errorMessage: string;
      if (
        challenge.game === 'trivia-misc-we' &&
        e.response?.data.message === 'You have attempted this challenge before!'
      ) {
        errorMessage = t('trivia.join.we.attempted-before');
      } else {
        errorMessage = e.response?.data?.message ?? t('app.errors.try-again');
      }

      if (isEmbedded) {
        window.parent?.postMessage({event: 'trivia.attempt.joining_failed', payload: {error: errorMessage}}, '*');
      }

      return toast({status: 'error', title: errorMessage});
    },
  });

  const onJoinChallenge = async (e: MouseEvent<HTMLButtonElement>) => {
    props.onClick?.(e);

    if (!isLoggedIn && !useGuestStore.getState().token) {
      await generateGuestTokenMutation.mutateAsync();
    }

    joinChallengeMutation.mutate();
  };

  return (
    <Button
      data-testid="join-challenge"
      flex={1}
      maxW="15rem"
      fontSize="md"
      fontWeight="700"
      gap="6"
      justifyContent="space-between"
      ps="6"
      pe="8"
      _loading={{justifyContent: 'center'}}
      {...props}
      isLoading={generateGuestTokenMutation.isLoading || joinChallengeMutation.isLoading}
      isDisabled={generateGuestTokenMutation.isLoading || joinChallengeMutation.isLoading}
      onClick={onJoinChallenge}
    >
      {t('trivia.join.cta')}

      <BackIcon transform="auto" scaleX={getLanguage(locale) === 'en' ? -1 : 1} width="2.5" strokeWidth="2px" />
    </Button>
  );
}
